import { useState, useCallback, useRef, useEffect } from 'react';
import { useProjectStore } from '@/store/projectStore';
import {
  exportVideo,
  saveVideoWithPicker,
  getSupportedFormats,
  EXPORT_FORMATS,
  ExportFormat,
  cancelExport,
} from '@/utils/videoExporter';
import {
  checkBackendAvailable,
  exportVideoViaBackend,
  cancelBackendExport,
} from '@/utils/backendExporter';
import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import {
  X,
  Download,
  Settings,
  Film,
  Volume2,
  Loader2,
  CheckCircle,
  AlertCircle,
  FileVideo,
  Zap,
  Image,
  RotateCcw,
  Star,
  Gauge,
} from 'lucide-react';

const resolutionPresets = {
  '720p': { width: 1280, height: 720, label: 'HD (720p)' },
  '1080p': { width: 1920, height: 1080, label: 'Full HD (1080p)' },
  '4k': { width: 3840, height: 2160, label: '4K UHD' },
  'original': { width: 0, height: 0, label: 'Original' },
};

const qualityPresets = {
  'low': { crf: 28, label: 'Low (smaller file)' },
  'medium': { crf: 23, label: 'Medium' },
  'high': { crf: 18, label: 'High (larger file)' },
};

const PHASE_LABELS: Record<string, string> = {
  uploading: 'Uploading video…',
  preparing: 'Preparing…',
  rendering: 'Rendering overlays…',
  encoding: 'Encoding video…',
  finalizing: 'Finalizing…',
  downloading: 'Downloading result…',
  complete: 'Complete!',
};

export const ExportPanel = () => {
  const [exportComplete, setExportComplete] = useState(false);
  const [savedFilePath, setSavedFilePath] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('mp4-h264');
  const [supportedFormats, setSupportedFormats] = useState<ExportFormat[]>([]);
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null);
  const exportingRef = useRef(false);

  const isOpen = useProjectStore((state) => state.isExportPanelOpen);
  const video = useProjectStore((state) => state.project.video);
  const videoBlob = useProjectStore((state) => state.project.videoBlob);
  const overlays = useProjectStore((state) => state.project.overlays);
  const trimRange = useProjectStore((state) => state.project.trimRange);
  const exportSettings = useProjectStore((state) => state.project.exportSettings);
  const renderProgress = useProjectStore((state) => state.renderProgress);

  const closeExportPanel = useProjectStore((state) => state.closeExportPanel);
  const setExportSettings = useProjectStore((state) => state.setExportSettings);
  const setRenderProgress = useProjectStore((state) => state.setRenderProgress);

  // Check browser-supported formats
  useEffect(() => {
    const formats = getSupportedFormats();
    setSupportedFormats(formats);
    if (formats.includes('mp4-h264')) {
      setSelectedFormat('mp4-h264');
    } else if (formats.length > 0) {
      setSelectedFormat(formats[0]);
    }
  }, []);

  // Check backend availability whenever panel opens
  useEffect(() => {
    if (!isOpen) return;
    setBackendAvailable(null); // show loading state
    checkBackendAvailable().then(setBackendAvailable);
  }, [isOpen]);

  // Estimated file size (accounts for trim)
  const calculateEstimatedSize = useCallback(() => {
    if (!video) return '-- MB';
    const resolution = resolutionPresets[exportSettings.resolution];
    const quality = qualityPresets[exportSettings.quality];
    const isPortrait = video.height > video.width;
    let width = resolution.width || video.width;
    let height = resolution.height || video.height;
    if (isPortrait && exportSettings.resolution !== 'original') {
      width = resolution.height;
      height = resolution.width;
    }
    const bitrate = (width * height * 0.07) / quality.crf;
    const effectiveDuration = trimRange?.enabled
      ? trimRange.endTime - trimRange.startTime
      : video.duration;
    const sizeBytes = (bitrate * effectiveDuration) / 8;
    if (sizeBytes > 1024 * 1024 * 1024) {
      return `~${(sizeBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
    return `~${(sizeBytes / (1024 * 1024)).toFixed(0)} MB`;
  }, [video, exportSettings, trimRange]);

  // ── Main export handler ───────────────────────────────────────────────────

  const handleExport = useCallback(async () => {
    if (!video || exportingRef.current) return;
    if (!videoBlob) {
      setRenderProgress({
        isRendering: false,
        progress: 0,
        error: 'Video file not available. Please re-import the video.',
      });
      return;
    }

    exportingRef.current = true;

    const isPortrait = video.height > video.width;
    let outputWidth = video.width;
    let outputHeight = video.height;

    if (exportSettings.resolution === '720p') {
      outputWidth = isPortrait ? 720 : 1280;
      outputHeight = isPortrait ? 1280 : 720;
    } else if (exportSettings.resolution === '1080p') {
      outputWidth = isPortrait ? 1080 : 1920;
      outputHeight = isPortrait ? 1920 : 1080;
    } else if (exportSettings.resolution === '4k') {
      outputWidth = isPortrait ? 2160 : 3840;
      outputHeight = isPortrait ? 3840 : 2160;
    }

    // Scale overlay bounds + font sizes to the output resolution
    const scaleX = outputWidth / video.width;
    const scaleY = outputHeight / video.height;

    const scaledOverlays = overlays.map((overlay) => ({
      ...overlay,
      bounds: {
        x: overlay.bounds.x * scaleX,
        y: overlay.bounds.y * scaleY,
        width: overlay.bounds.width * scaleX,
        height: overlay.bounds.height * scaleY,
      },
      // Polygon points are offsets inside the bounds, so they have to be
      // scaled with them — otherwise a polygon exported at anything but the
      // source resolution kept its original size inside a resized box and no
      // longer matched the shape drawn in the preview.
      ...((overlay as any).polygon
        ? {
            polygon: {
              ...(overlay as any).polygon,
              points: (overlay as any).polygon.points.map((p: { x: number; y: number }) => ({
                x: p.x * scaleX,
                y: p.y * scaleY,
              })),
            },
          }
        : {}),
      // Tile gap is a distance in video pixels like the bounds are, so leaving
      // it unscaled made tiled watermarks bunch up (or spread out) in the
      // export relative to the preview.
      ...((overlay as any).tile
        ? {
            tile: {
              ...(overlay as any).tile,
              gap: (overlay as any).tile.gap * scaleX,
            },
          }
        : {}),
      ...(overlay.type === 'text'
        ? {
            text: {
              ...(overlay as any).text,
              fontSize: (overlay as any).text.fontSize * scaleY,
            },
          }
        : {}),
    }));

    const effectiveDuration = trimRange?.enabled
      ? trimRange.endTime - trimRange.startTime
      : video.duration;

    setRenderProgress({
      isRendering: true,
      progress: 0,
      currentPhase: 'preparing',
      estimatedTimeRemaining: effectiveDuration * 2,
    });

    const onProgress = (progress: number, phase: string) => {
      setCurrentPhase(phase);
      setRenderProgress({
        isRendering: true,
        progress,
        currentPhase: phase as any,
        estimatedTimeRemaining: Math.max(
          0,
          ((100 - progress) / 100) * effectiveDuration * 1.5
        ),
      });
    };

    const runBrowserExport = async (): Promise<{ blob: Blob; extension: string }> => {
      const videoElement = document.querySelector('video') as HTMLVideoElement;
      if (!videoElement) throw new Error('Could not find video element. Please try again.');
      const result = await exportVideo({
        video: videoElement,
        videoBlob,
        overlays: scaledOverlays as any,
        width: outputWidth,
        height: outputHeight,
        quality: exportSettings.quality,
        format: selectedFormat,
        trimRange: trimRange ?? null,
        onProgress,
      });
      const blob = result.blob;
      URL.revokeObjectURL(result.url);
      return { blob, extension: result.extension };
    };

    try {
      let outputBlob: Blob;
      let extension = '.mp4';

      if (backendAvailable) {
        // ── Fast path: FFmpeg backend (20–100× faster) ──────────────────
        try {
          outputBlob = await exportVideoViaBackend({
            videoBlob,
            videoName: video.name ?? 'video.mp4',
            overlays: scaledOverlays as any,
            sourceWidth: video.width,
            sourceHeight: video.height,
            exportSettings,
            trimRange: trimRange ?? null,
            onProgress,
          });
          extension = exportSettings.format === 'gif' ? '.gif' : '.mp4';
        } catch (backendError: any) {
          if (backendError?.message === 'Export cancelled') throw backendError;
          // Backend failed — silently fall back to browser MediaRecorder
          console.warn('Backend export failed, falling back to browser export:', backendError);
          onProgress(0, 'preparing');
          const result = await runBrowserExport();
          outputBlob = result.blob;
          extension = result.extension;
        }
      } else {
        // ── Slow path: browser MediaRecorder fallback ───────────────────
        const result = await runBrowserExport();
        outputBlob = result.blob;
        extension = result.extension;
      }

      // Save to disk
      const baseName = (video.name ?? 'export').replace(/\.[^/.]+$/, '');
      const suggestedName = `${baseName}_edited${extension}`;
      const savedName = await saveVideoWithPicker(outputBlob, suggestedName, extension);

      if (savedName) {
        setSavedFilePath(savedName);
        setExportComplete(true);
      }

      setRenderProgress({
        isRendering: false,
        progress: 100,
        currentPhase: 'finalizing',
        estimatedTimeRemaining: 0,
      });
    } catch (error: any) {
      if (error?.message === 'Export cancelled') {
        setRenderProgress({
          isRendering: false,
          progress: 0,
          currentPhase: 'preparing',
        });
      } else {
        console.error('Export error:', error);
        setRenderProgress({
          isRendering: false,
          progress: 0,
          error: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    } finally {
      exportingRef.current = false;
    }
  }, [
    video,
    videoBlob,
    overlays,
    exportSettings,
    selectedFormat,
    trimRange,
    backendAvailable,
    setRenderProgress,
  ]);

  // Cancel
  const handleCancel = useCallback(async () => {
    cancelExport();
    await cancelBackendExport();
    exportingRef.current = false;
    setRenderProgress({
      isRendering: false,
      progress: 0,
      currentPhase: 'preparing',
    });
  }, [setRenderProgress]);

  // Close and reset
  const handleClose = useCallback(async () => {
    cancelExport();
    await cancelBackendExport();
    exportingRef.current = false;
    setExportComplete(false);
    setSavedFilePath(null);
    setCurrentPhase('');
    setRenderProgress({
      isRendering: false,
      progress: 0,
      currentPhase: 'preparing',
      error: undefined,
    });
    closeExportPanel();
  }, [closeExportPanel, setRenderProgress]);

  if (!video) return null;

  const isPortrait = video.height > video.width;
  const formatConfig = EXPORT_FORMATS[selectedFormat];

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] max-h-[90vh] overflow-y-auto bg-editor-panel border border-editor-border rounded-xl z-50 animate-slide-up">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-editor-border sticky top-0 bg-editor-panel z-10">
            <Dialog.Title className="text-lg font-semibold flex items-center gap-2">
              <Download size={20} className="text-editor-accent" />
              Export Video
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="btn btn-ghost btn-icon"
                disabled={renderProgress.isRendering}
                onClick={handleClose}
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div className="p-6">
            {exportComplete ? (
              /* ── Done ── */
              <div className="text-center space-y-6 py-4">
                <CheckCircle className="w-16 h-16 mx-auto text-editor-success" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Export Complete</h3>
                  {savedFilePath && (
                    <p className="text-sm text-editor-text-muted">Saved as: {savedFilePath}</p>
                  )}
                </div>
                <button onClick={handleClose} className="btn btn-primary w-full py-3">
                  Done
                </button>
              </div>
            ) : renderProgress.isRendering ? (
              /* ── Progress ── */
              <div className="space-y-6">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 mx-auto mb-4 text-editor-accent animate-spin" />
                  <h3 className="text-lg font-medium mb-1">
                    {PHASE_LABELS[currentPhase] ?? PHASE_LABELS[renderProgress.currentPhase] ?? 'Processing…'}
                  </h3>
                  <p className="text-sm text-editor-text-muted">
                    {renderProgress.progress < 100 && (
                      <>Estimated time remaining: {Math.ceil(renderProgress.estimatedTimeRemaining || 0)}s</>
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="h-2 bg-editor-surface rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-editor-accent to-purple-500 transition-all duration-300"
                      style={{ width: `${renderProgress.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-editor-text-muted">
                    <span>{Math.round(renderProgress.progress)}%</span>
                    <span className="capitalize">{currentPhase || renderProgress.currentPhase}</span>
                  </div>
                </div>

                <button onClick={handleCancel} className="btn btn-secondary w-full">
                  Cancel
                </button>
              </div>
            ) : renderProgress.error ? (
              /* ── Error ── */
              <div className="text-center space-y-4">
                <AlertCircle className="w-12 h-12 mx-auto text-editor-error" />
                <div>
                  <h3 className="text-lg font-medium mb-1">Export Failed</h3>
                  <p className="text-sm text-editor-text-muted">{renderProgress.error}</p>
                </div>
                <button
                  onClick={() => setRenderProgress({ error: undefined })}
                  className="btn btn-primary w-full"
                >
                  Try Again
                </button>
              </div>
            ) : (
              /* ── Settings ── */
              <div className="space-y-6">

                {/* Engine badge */}
                <div
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
                    backendAvailable === null
                      ? 'bg-editor-surface text-editor-text-muted'
                      : backendAvailable
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  )}
                >
                  {backendAvailable === null ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : backendAvailable ? (
                    <Zap size={14} />
                  ) : (
                    <AlertCircle size={14} />
                  )}
                  <span>
                    {backendAvailable === null
                      ? 'Checking export engine…'
                      : backendAvailable
                        ? 'Fast export engine active (FFmpeg — up to 100× faster)'
                        : 'Using browser export (start the backend for 100× faster exports)'}
                  </span>
                </div>

                {/* Format — only shown in browser fallback mode */}
                {!backendAvailable && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium mb-3">
                      <FileVideo size={16} className="text-editor-text-muted" />
                      Format
                    </label>
                    <div className="space-y-2">
                      {(Object.keys(EXPORT_FORMATS) as ExportFormat[]).map((format) => {
                        const config = EXPORT_FORMATS[format];
                        const isSupported = supportedFormats.includes(format);
                        return (
                          <button
                            key={format}
                            onClick={() => isSupported && setSelectedFormat(format)}
                            disabled={!isSupported}
                            className={clsx(
                              'w-full px-4 py-3 rounded-lg border text-left transition-all',
                              selectedFormat === format
                                ? 'border-editor-accent bg-editor-accent/10'
                                : isSupported
                                  ? 'border-editor-border bg-editor-surface hover:border-editor-hover'
                                  : 'border-editor-border bg-editor-surface/50 opacity-50 cursor-not-allowed'
                            )}
                          >
                            <div className="flex items-center justify-between">
                              <span
                                className={clsx(
                                  'font-medium text-sm',
                                  selectedFormat === format
                                    ? 'text-editor-text'
                                    : 'text-editor-text-muted'
                                )}
                              >
                                {config.label}
                              </span>
                              {format === 'mp4-h264' && (
                                <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                                  Recommended
                                </span>
                              )}
                              {!isSupported && (
                                <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                                  Not Supported
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-editor-text-dim mt-1">{config.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Resolution */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-3">
                    <Film size={16} className="text-editor-text-muted" />
                    Resolution
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(resolutionPresets) as Array<keyof typeof resolutionPresets>).map((key) => {
                      const preset = resolutionPresets[key];
                      const width = isPortrait && key !== 'original' ? preset.height : preset.width;
                      const height = isPortrait && key !== 'original' ? preset.width : preset.height;
                      return (
                        <button
                          key={key}
                          onClick={() => setExportSettings({ resolution: key })}
                          className={clsx(
                            'px-4 py-3 rounded-lg border text-sm text-left transition-all',
                            exportSettings.resolution === key
                              ? 'border-editor-accent bg-editor-accent/10 text-editor-text'
                              : 'border-editor-border bg-editor-surface text-editor-text-muted hover:border-editor-hover'
                          )}
                        >
                          <span className="block font-medium">{preset.label}</span>
                          {key !== 'original' ? (
                            <span className="text-xs text-editor-text-dim">
                              {width} × {height}
                            </span>
                          ) : (
                            <span className="text-xs text-editor-text-dim">
                              {video.width} × {video.height}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Quality */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-3">
                    <Settings size={16} className="text-editor-text-muted" />
                    Quality
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {(Object.keys(qualityPresets) as Array<keyof typeof qualityPresets>).map((key) => (
                      <button
                        key={key}
                        onClick={() => setExportSettings({ quality: key })}
                        className={clsx(
                          'px-4 py-3 rounded-lg border text-sm transition-all',
                          exportSettings.quality === key
                            ? 'border-editor-accent bg-editor-accent/10 text-editor-text'
                            : 'border-editor-border bg-editor-surface text-editor-text-muted hover:border-editor-hover'
                        )}
                      >
                        <span className="capitalize">{key}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Draft / Quick-Preview Mode (2G) */}
                <div className={clsx(
                  'flex items-start gap-3 py-3 px-4 rounded-lg border transition-all',
                  exportSettings.isDraft
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : 'bg-editor-surface border-editor-border'
                )}>
                  <Gauge size={16} className={clsx('mt-0.5', exportSettings.isDraft ? 'text-yellow-400' : 'text-editor-text-muted')} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">Draft / Quick Preview</span>
                        {exportSettings.isDraft && (
                          <span className="ml-2 text-[10px] bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                            720p · CRF 35 · ultrafast
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setExportSettings({ isDraft: !exportSettings.isDraft })}
                        className={clsx(
                          'w-11 h-6 rounded-full transition-colors relative flex-shrink-0',
                          exportSettings.isDraft ? 'bg-yellow-500' : 'bg-editor-border'
                        )}
                      >
                        <div className={clsx('absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                          exportSettings.isDraft ? 'translate-x-6' : 'translate-x-1')} />
                      </button>
                    </div>
                    <p className="text-xs text-editor-text-dim mt-1">
                      Forces 720p, lower quality, and ultrafast encoding for near-instant preview exports. Disable for final delivery.
                    </p>
                  </div>
                </div>

                {/* Output Format */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-3">
                    <Image size={16} className="text-editor-text-muted" />
                    Output Format
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['mp4', 'gif'] as const).map((fmt) => (
                      <button
                        key={fmt}
                        onClick={() => setExportSettings({ format: fmt })}
                        className={clsx(
                          'px-4 py-3 rounded-lg border text-sm text-left transition-all',
                          exportSettings.format === fmt
                            ? 'border-editor-accent bg-editor-accent/10 text-editor-text'
                            : 'border-editor-border bg-editor-surface text-editor-text-muted hover:border-editor-hover'
                        )}
                      >
                        <span className="block font-medium uppercase">{fmt}</span>
                        <span className="text-xs text-editor-text-dim">
                          {fmt === 'mp4' ? 'Video with audio' : 'Animated GIF (no audio)'}
                        </span>
                      </button>
                    ))}
                  </div>

                </div>

                {/* GIF-specific settings */}
                {exportSettings.format === 'gif' && (
                  <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg space-y-3">
                    <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide flex items-center gap-1">
                      <RotateCcw size={12} /> GIF Settings
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-editor-text-muted">Frame rate</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="range" min={4} max={24} step={2}
                          value={exportSettings.gifFps ?? 12}
                          onChange={(e) => setExportSettings({ gifFps: +e.target.value })}
                          className="w-24 accent-amber-400"
                        />
                        <span className="text-sm text-editor-text w-10 text-right">
                          {exportSettings.gifFps ?? 12} fps
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-editor-text-muted">Loop</span>
                      <button
                        onClick={() => setExportSettings({ gifLoop: exportSettings.gifLoop === 0 ? 1 : 0 })}
                        className={clsx(
                          'w-11 h-6 rounded-full transition-colors relative',
                          (exportSettings.gifLoop ?? 0) === 0 ? 'bg-amber-500' : 'bg-editor-border'
                        )}
                      >
                        <div className={clsx('absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                          (exportSettings.gifLoop ?? 0) === 0 ? 'translate-x-6' : 'translate-x-1')} />
                      </button>
                    </div>
                    <p className="text-xs text-amber-400/70">
                      {(exportSettings.gifLoop ?? 0) === 0 ? 'Loops infinitely' : 'Plays once'}
                      {' · '}GIFs may be larger than MP4 at the same duration.
                    </p>
                  </div>
                )}

                {/* Audio (MP4 only) */}
                {exportSettings.format === 'mp4' && (
                  <div className="flex items-center justify-between py-3 px-4 bg-editor-surface rounded-lg">
                    <div className="flex items-center gap-2">
                      <Volume2 size={16} className="text-editor-text-muted" />
                      <span className="text-sm">Preserve audio</span>
                    </div>
                    <button
                      onClick={() =>
                        setExportSettings({ preserveAudio: !exportSettings.preserveAudio })
                      }
                      className={clsx(
                        'w-11 h-6 rounded-full transition-colors relative',
                        exportSettings.preserveAudio ? 'bg-editor-accent' : 'bg-editor-border'
                      )}
                    >
                      <div
                        className={clsx(
                          'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                          exportSettings.preserveAudio ? 'translate-x-6' : 'translate-x-1'
                        )}
                      />
                    </button>
                  </div>
                )}

                {/* Made with Overlay Studio branding */}
                <div className={clsx(
                  'flex items-start gap-3 py-3 px-4 rounded-lg border transition-all',
                  exportSettings.addBranding
                    ? 'bg-indigo-500/10 border-indigo-500/30'
                    : 'bg-editor-surface border-editor-border'
                )}>
                  <Star size={16} className={clsx('mt-0.5', exportSettings.addBranding ? 'text-indigo-400' : 'text-editor-text-muted')} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Made with Overlay Studio</span>
                      <button
                        onClick={() => setExportSettings({ addBranding: !exportSettings.addBranding })}
                        className={clsx(
                          'w-11 h-6 rounded-full transition-colors relative flex-shrink-0',
                          exportSettings.addBranding ? 'bg-indigo-500' : 'bg-editor-border'
                        )}
                      >
                        <div className={clsx('absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                          exportSettings.addBranding ? 'translate-x-6' : 'translate-x-1')} />
                      </button>
                    </div>
                    <p className="text-xs text-editor-text-dim mt-1">
                      Add a small watermark to help others discover Overlay Studio.
                    </p>
                  </div>
                </div>


                {/* Summary */}
                <div className="p-4 bg-editor-surface rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-editor-text-muted">Output format</span>
                    <span className={clsx('font-medium', exportSettings.isDraft ? 'text-yellow-400' : 'text-editor-text')}>
                      {exportSettings.format === 'gif'
                        ? `GIF (${exportSettings.gifFps ?? 12} fps)`
                        : exportSettings.isDraft
                          ? 'MP4 Draft (720p · ultrafast)'
                          : backendAvailable ? 'MP4 (H.264 via FFmpeg)' : formatConfig.label.split(' - ')[0]}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-editor-text-muted">Overlays</span>
                    <span className="text-editor-text">
                      {overlays.length} element{overlays.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-editor-text-muted">Duration</span>
                    <span
                      className={clsx(
                        'text-editor-text',
                        trimRange?.enabled && 'text-yellow-400'
                      )}
                    >
                      {trimRange?.enabled
                        ? `${(trimRange.endTime - trimRange.startTime).toFixed(1)}s (trimmed)`
                        : `${video.duration.toFixed(1)}s`}
                    </span>
                  </div>
                  {trimRange?.enabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-editor-text-muted">Trim range</span>
                      <span className="text-yellow-400 text-xs">
                        {trimRange.startTime.toFixed(1)}s → {trimRange.endTime.toFixed(1)}s
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-editor-text-muted">Estimated size</span>
                    <span className="text-editor-text font-medium">{calculateEstimatedSize()}</span>
                  </div>
                </div>

                {/* Export button */}
                <button
                  onClick={handleExport}
                  className={clsx(
                    'btn w-full py-3',
                    exportSettings.isDraft ? 'bg-yellow-500 hover:bg-yellow-400 text-black font-semibold' : 'btn-primary'
                  )}
                  disabled={!backendAvailable && supportedFormats.length === 0}
                >
                  {exportSettings.isDraft ? (
                    <>
                      <Gauge size={18} className="mr-2" />
                      Quick Preview Export
                    </>
                  ) : (
                    <>
                      <Download size={18} className="mr-2" />
                      Start Export
                      {backendAvailable && (
                        <span className="ml-2 text-xs opacity-70 flex items-center gap-1">
                          <Zap size={11} /> Fast
                        </span>
                      )}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
